import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MatButtonModule,
  MatCheckboxModule,
  MatDialogModule,
  MatFormFieldModule,
  MatIconModule,
  MatInputModule,
  MatMenuModule,
  MatSelectModule,
  MatStepperModule,
  MatToolbarModule,
  MatTooltipModule
} from '@angular/material';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from './components/header/header.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { AngularFontAwesomeModule } from 'angular-font-awesome';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { FooterComponent } from './components/footer/footer.component';
import { RedirectComponent } from './components/redirect/redirect.component';
import { CarouselComponent } from './components/carousel/carousel.component';
import { CarouselItemDirective } from './components/carousel/carousel-item.directive';
import { CarouselItemElementDirective } from './components/carousel/carousel-item-element.directive';
import { CaseDetailComponent } from './components/case-detail/case-detail.component';
import { CaseListComponent } from './components/case-list/case-list.component';
import { CaseSearchComponent } from './components/case-search/case-search.component';
import { ChunkPipe } from './components/pipes/chunk.pipe';
import { NeedsReviewFilterPipe } from './components/pipes/needs-review-filter.pipe';
import { PaginatePipe } from './components/pipes/paginate.pipe';
import { CrisisAlertRoutingModule } from './crisis-alert-routing.module';
import { CommonPipeModule } from '../common-pipe/common-pipe.module';
import { DashboardCarouselComponent } from './dashboard/dashboard-carousel/dashboard-carousel.component';
import { DashboardCardComponent } from './dashboard/dashboard-card/dashboard-card.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { TotalNeedsReviewCountPipe } from './components/pipes/total-needs-review-count.pipe';
import { DashboardSummaryComponent } from './dashboard/dashboard-summary/dashboard-summary.component';
import { TermsConditionsComponent } from './components/terms-conditions/terms-conditions.component';
import { SelectMultipleComponent } from './components/select-multiple/select-multiple.component';
import { ContentTrackingModalComponent } from './components/content-tracking-modal/content-tracking-modal.component';
import { AirViewerComponent } from './components/air-viewer/air-viewer.component';
import { AssignmentExpirationModalComponent } from './components/assignment-expiration-modal/assignment-expiration-modal.component';
import { SignoutDialogComponent } from './components/signout-dialog/signout-dialog.component';
import { MenuComponent } from './components/menu/menu.component';
import { ProgramInfoPipe } from './components/pipes/program-info.pipe';
import { ProgramGuidePipe } from './components/pipes/program-guide.pipe';
import { ExportComponent } from './components/export/export.component';
import { ExportFileSearchComponent } from './components/export-file-search/export-file-search.component';
import { ExportFileListComponent } from './components/export-file-list/export-file-list.component';
import { DownloadDialogComponent } from './components/download-dialog/download-dialog.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { DownloadMiniDialogComponent } from './components/download-mini-dialog/download-mini-dialog.component';
import { MatGridListModule } from '@angular/material/grid-list';
import { TextViewerComponent } from './components/text-viewer/text-viewer.component';
import { ImageViewerComponent } from './components/image-viewer/image-viewer.component';
import { ErrorNotFoundComponent } from './components/error-not-found/error-not-found.component';
import { KalturaAssetPlayerComponent } from './components/kaltura-asset-player/kaltura-asset-player.component';
import { AudioPlayerComponent } from './components/audio-player/audio-player.component';
import { KalturaComponent } from './components/kaltura/kaltura.component';
import { UploadComponent } from './components/upload/upload.component';
import { FileTabGroupComponent } from './components/file-tab-group/file-tab-group.component';
import {MatTabsModule} from '@angular/material/tabs';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatNativeDateModule} from '@angular/material/core';
import { UploadDialogComponent } from './components/upload-dialog/upload-dialog.component';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import { SpinnerDialogComponent } from './components/spinner-dialog/spinner-dialog.component';

/**
 * Shared Module declares often used components, directives and pipes.
 * Export these declarations so they will be accessible inside any
 * module that imports the SharedModule.Furthermore, if you’re importing
 * modules that make declarations which need to be available across the
 * entire app(like FormsModule, CommonModule, or a 3rd-party modules),
 * just import and export them in the SharedModule.The SharedModule
 * should NOT be providing any services, as these could be instantiated
 * more than once when importing the SharedModule multiple times.
 */

@NgModule({
  declarations: [
    /**
     * Declare often used components, directives and pipes here.
     * Then export these declarations and they will be accessible
     * inside any module that imports the SharedModule.
     */
    HeaderComponent,
    SignoutDialogComponent,
    MenuComponent,
    DashboardComponent,
    FooterComponent,
    RedirectComponent,
    CarouselComponent,
    CaseListComponent,
    CaseSearchComponent,
    ChunkPipe,
    DashboardCarouselComponent,
    DashboardSummaryComponent,
    CaseDetailComponent,
    PaginatePipe,
    DashboardCardComponent,
    CarouselItemDirective,
    CarouselItemElementDirective,
    NeedsReviewFilterPipe,
    TotalNeedsReviewCountPipe,
    TermsConditionsComponent,
    SelectMultipleComponent,
    ContentTrackingModalComponent,
    AirViewerComponent,
    AssignmentExpirationModalComponent,
    ProgramInfoPipe,
    ProgramGuidePipe,
    ExportComponent,
    ExportFileSearchComponent,
    ExportFileListComponent,
    DownloadDialogComponent,
    DownloadMiniDialogComponent,
    TextViewerComponent,
    ImageViewerComponent,
    ErrorNotFoundComponent,
    KalturaAssetPlayerComponent,
    AudioPlayerComponent,
    KalturaComponent,
    UploadComponent,
    FileTabGroupComponent,
    UploadDialogComponent,
    SpinnerDialogComponent
  ],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    NgbModule,
    AngularFontAwesomeModule,
    CrisisAlertRoutingModule,
    CommonPipeModule,
    BrowserModule,
    BrowserAnimationsModule,
    MatTooltipModule,
    MatStepperModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCheckboxModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatToolbarModule,
    MatMenuModule,
    MatProgressBarModule,
    MatGridListModule,
    MatTabsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule
  ],
  bootstrap: [
    DashboardCarouselComponent,
    ContentTrackingModalComponent,
    AssignmentExpirationModalComponent,
    SignoutDialogComponent
  ],
  exports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    HeaderComponent,
    MenuComponent,
    DashboardComponent,
    FooterComponent,
    MatTooltipModule,
    MatStepperModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCheckboxModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatToolbarModule,
    MatMenuModule,
    DownloadDialogComponent
  ],
  entryComponents: [
    DownloadDialogComponent,
    DownloadMiniDialogComponent,
    UploadDialogComponent,
    SpinnerDialogComponent
  ],
})
export class SharedModule {
}
